import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
	faArchive,
	faBan,
	faBolt,
	faChevronDown,
	faChevronUp,
	faClock,
	faMessage,
	faPaperPlane,
	faPlus,
	faSave,
	faShareNodes,
	faTrash,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";

import { isGraphConnected } from "@signalist/shared-types";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { Icon, Modal } from "@/components";

import { classNames } from "@/utils/classNames";
import { toPriorityFilterItems } from "@/utils/priorityFilterOptions";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { CopywritingStatus } from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { Information } from "@/components/Information/Information";
import { useOutreachActivation } from "@/components/OutreachActivation/useOutreachActivation";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useSearch } from "@/utils";
import {
	clearInbox,
	listSelector,
	setCurrentLeadIndex,
	updatePersonInInbox,
} from "@/pages/Private/redux/list/list.slice";
import {
	AssignStatus,
	AssignStatusFilter,
	InclusionFilterStatus,
	ListSourceType,
	PriorityFilterStatus,
	SignalFilterStatus,
} from "@/enum/list.enum";
import {
	useLazyGetAllProspectsQuery,
	useSnoozeProspectMutation,
	useUpdateListPersonMutation,
	useUpdateListPersonSendersMutation,
} from "@/pages/Private/redux/list/list.api";
import { useGetReviewSessionQuery } from "@/pages/Private/redux/reviewSession/reviewSession.api";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGetCompanyLifecycleQuery } from "@/pages/Private/redux/company/company.api";

import { InputField } from "@/components/InputField/InputField";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { NoSenderModal } from "@/components/NoSenderModal/NoSenderModal";

import { SendersInfo } from "../../../List/components/CampaignOverview/components/Lead/components/SendersInfo";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { GlobalNodesDataProvider } from "../../../List/components/CampaignOverview/components/NodesContextProvider";
import { FilterModal, IFilterWithValues } from "../../../List/components/FilterModal";
import { ListPerson } from "../../../List/schema/list";
import { isDummyProspect } from "../../../List/utils/columns/dummyProspect";
import { CampaignMessagingOverview } from "../CampaignMessagingOverview/CampaignMessagingOverview";
import { CopywritingCampaign } from "../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { useBulkAssign } from "../../../List/utils/useBulkAssign";
import { AddToTrackerFooterButton } from "../../components/AddToTrackerFooterButton/AddToTrackerFooterButton";
import { LeadDetailRail } from "../../components/LeadDetailRail";
import { Sender } from "../../../List/components/CampaignOverview/components/Sender";

import { hasActiveSender } from "./utils/senders";
import { CutoffModal } from "./components/CutoffModal";
import { AssignCampaignPickerModal } from "./components/AssignCampaignPickerModal";
import { validCampaignsOfLead } from "./utils/validCampaignsOfLead";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { ListViewSwitcher } from "../../../List/components/ListViewSwitcher";
import { ListContextHeader } from "../../components/ListContextHeader";
import { ListScopeFilter } from "../../components/ListScopeFilter/ListScopeFilter";
import { ListScope } from "../../components/ListScopeFilter/listScope";
import { useListScopeLists } from "../../components/ListScopeFilter/useListScopeLists";
import { MessagesFilterBar } from "../../components/MessagesFilterBar/MessagesFilterBar";
import { ReadOnlyPlanBanner } from "../../components/ReadOnlyPlanBanner";
import { resolveMessagesAccessMode } from "../../utils/messagesAccessMode";
import { useLeadsPagingSentinel } from "../../utils/useLeadsPagingSentinel";

import { useAddInboxChat } from "../../../List/components/CampaignOverview/utils/useAddInboxChat";
import { useFeedDraftGeneration } from "../../../List/components/CampaignOverview/utils/useFeedDraftGeneration";
import { getListCampaignConfig } from "../../utils/getListCampaignConfig";
import {
	buildDefaultCampaignSenders,
	campaignSendersFor,
	mergeCurrentCampaignSenders,
	toSenderIdentities,
} from "../../utils/campaignSenders";
import { useSenderConfirmGate } from "../../../List/components/CampaignOverview/utils/useSenderConfirmGate";
import { resolveSelectedCampaignConfig } from "../../utils/resolveSelectedCampaignConfig";
import { SignpostCard } from "../../components/SignpostCard/SignpostCard";
import { prospectHasCampaign } from "../../utils/prospectHasCampaign";
import { getInitialAssignStatusFilter } from "../../utils/readyToSendAssignStatus";
import { getInitialPriorityFilter } from "../../utils/readyToSendPriorityFilter";
import { useCampaignStatusPolling } from "../../../List/hooks/useCampaignStatusPolling";
import { useReadyToSendUrlSync } from "./useReadyToSendUrlSync";
import { buildReadyToSendRequestParams } from "./utils/readyToSendRequestParams";
import { ReadyToSendLeadsColumn } from "./ReadyToSendLeadsColumn";

enum Tabs {
	CHAT = "CHAT",
	CAMPAIGN = "CAMPAIGN",
	// Retained for compatibility with the shared Lead card's handleTabsChange prop type; the Feed
	// tab itself is no longer rendered on Review-to-send.
	FEED = "FEED",
}

export const ReadyToSend: FunctionComponent = () => {
	const { t } = useTranslation();

	const { type = "ready-to-send" } = useParams();

	const [snoozedUntil, setSnoozedUntil] = useState<Date>(new Date());
	// Shown before opening the picker when the lead has Chat drafts (pre-assigning supersedes them).
	const [showDiscardDraftsModal, setShowDiscardDraftsModal] = useState<boolean>(false);
	const { data: sendingUsers } = useGetSendersUsersQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const { data: lifecycle } = useGetCompanyLifecycleQuery();
	const accessMode = resolveMessagesAccessMode({
		lifecycleState: lifecycle?.state,
		hasSendingUsers: (sendingUsers?.data?.length ?? 0) > 0,
		sendingUsersLoaded: Boolean(sendingUsers?.data),
	});

	const initialTabFromUrl = (): Tabs => {
		const params = new URLSearchParams(location.search);
		const value = params.get("initialTab");

		if (!value) {
			return Tabs.CAMPAIGN;
		}

		const upper = value.toUpperCase();

		if (upper === Tabs.CHAT || upper === Tabs.CAMPAIGN) {
			return upper as Tabs;
		}

		return Tabs.CAMPAIGN;
	};

	const [currentTab, setCurrentTab] = useState<Tabs>(initialTabFromUrl());

	const handleTabsChange = useCallback((value: Tabs): void => {
		setCurrentTab(value);

		if (value === Tabs.CHAT) {
			setTimeout(() => {
				scrollToBottom();
			}, 100);
		}
	}, []);

	const [selectedAssignStatus, setSelectedAssignStatus] = useState<AssignStatusFilter[]>(
		getInitialAssignStatusFilter(location.search)
	);

	const reviewSessionIdFromUrl = (): string | null => {
		const params = new URLSearchParams(location.search);

		return params.get("reviewSessionId");
	};

	const [reviewSessionId] = useState<string | null>(reviewSessionIdFromUrl());

	const { data: reviewSession, isLoading: isReviewSessionLoading } = useGetReviewSessionQuery(
		reviewSessionId ?? "",
		{ skip: !reviewSessionId }
	);

	const [snoozeProspect, { isLoading: isSnoozingProspect }] = useSnoozeProspectMutation();

	const inboxChat = useAddInboxChat({ prefillDrafts: true });
	const { renderChatlog, renderSendButtons, scrollToBottom } = inboxChat;

	const { renderFeedDraftSnackbar } = useFeedDraftGeneration();

	const [configureSenders, setConfigureSenders] = useState<boolean>(false);

	const { sortingInbox, inbox, currentLeadIndex, meta } = useAppSelector(listSelector);

	const prevLeadIndexRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		const prospect = inbox[currentLeadIndex];

		if (!prospect) {
			return;
		}

		if (prevLeadIndexRef.current === currentLeadIndex && currentTab !== undefined) {
			return;
		}

		prevLeadIndexRef.current = currentLeadIndex;

		const hasCampaign = prospectHasCampaign(prospect);

		if (hasCampaign) {
			setCurrentTab(Tabs.CAMPAIGN);
		} else {
			setCurrentTab(Tabs.CHAT);

			setTimeout(() => {
				scrollToBottom();
			}, 1);
		}
	}, [inbox, currentLeadIndex]);

	const defaultSenders = useMemo<Sender[]>(
		() => buildDefaultCampaignSenders(sendingUsers?.data),
		[sendingUsers]
	);

	const currentSenders = useMemo<Sender[]>(
		() => mergeCurrentCampaignSenders(defaultSenders, inbox[currentLeadIndex]),
		[inbox, currentLeadIndex, defaultSenders]
	);

	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	// Whether the "configure who is sending" section inside the schedule-confirm modal is expanded.
	// Collapsed by default; auto-expanded when opening the modal with no active sender set.
	const [showSenderConfig, setShowSenderConfig] = useState<boolean>(false);
	// The "Assign to campaign" picker (campaign rows + Only pre-assign / Assign & send out), opened
	// from the lead card / hover / signpost. Separate from the bottom "Send sequence" confirm.
	const [showAssignPicker, setShowAssignPicker] = useState<boolean>(false);
	// "Assign & send out" / "Confirm & send" ask to start a campaign that is not sending (PREC-3296).
	const { assignWithActivation, activationModal } = useOutreachActivation();
	// A campaign send that goes out as colleagues asks first, once, listing each (PREC-4312, Q5).
	const { confirmSenders, senderConfirmModal } = useSenderConfirmGate();

	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
	const [showCheckLaterModal, setShowCheckLaterModal] = useState<boolean>(false);
	const [showSnoozeModal, setShowSnoozeModal] = useState<boolean>(false);
	// Lead-level "Stop sending after date" cut-off.
	const [showCutoffModal, setShowCutoffModal] = useState<boolean>(false);
	const [cutoffDate, setCutoffDate] = useState<string>("");

	// Review to send is single-select (multi-select lives on the List page). Kept as a
	// constant empty array so the existing single-lead guards (`selected.length === 0`) read true.
	const selected: number[] = [];

	const {
		handleBulkReassignInternalCampaigns,
		bulkReassignIsLoading,
		handleBulkStatusChange,
		bulkChangeStatusIsLoading,
		createBlacklistIsLoading,
		renderBlacklistModal,
		setShowBlacklistModal,
		renderAddToTrackerModal,
		setShowAddToTrackerModal,
		bulkAddToTrackerIsLoading,
	} = useBulkAssign();

	useCampaignStatusPolling();

	const [currentErrors, setCurrentErrors] = useState<string[]>([]);

	const [customCampaignConfig, setCustomCampaignConfig] = useState<CopywritingCampaign | null>(
		null
	);

	const [selectedCampaignConfig, setSelectedCampaignConfig] = useState<
		(CopywritingCampaign & { isCustom: boolean }) | null | undefined
	>(null);

	const getSignalFilterStatusFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return params.get("signalFilterStatus")
			? (JSON.parse(params.get("signalFilterStatus") || "[]") as SignalFilterStatus[])
			: [SignalFilterStatus.ALL];
	};

	const {
		lists: listData,
		filterProps: listFilterProps,
		resetPage: resetListPage,
	} = useListScopeLists(ListScope.READY_TO_SEND);

	const getSelectedListsFromUrl = (): CustomListsAutocompleteForSequencerItem[] => {
		const params = new URLSearchParams(location.search);
		const listIdsParam = params.get("listIds");

		return listIdsParam
			? (JSON.parse(listIdsParam) as CustomListsAutocompleteForSequencerItem[])
			: [];
	};

	const [selectedLists, setSelectedLists] =
		useState<CustomListsAutocompleteForSequencerItem[]>(getSelectedListsFromUrl());

	const [priorityFilterStatus, setPriorityFilterStatus] = useState<PriorityFilterStatus[]>(
		getInitialPriorityFilter(location.search)
	);

	const [signalFilterStatus, setSignalFilterStatus] = useState<SignalFilterStatus[]>(
		getSignalFilterStatusFromUrl()
	);

	const navigate = useNavigate();

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const leadsEndRef = useRef<HTMLDivElement>(null);
	const [updateListPerson] = useUpdateListPersonMutation();
	const [updateListPersonSenders] = useUpdateListPersonSendersMutation();

	const dispatch = useAppDispatch();

	const [
		getUnassigned,
		{ isFetching: isFetchingGetUnassigned, isLoading: isLoadingGetUnassigned },
	] = useLazyGetAllProspectsQuery();

	const { limit, query, setSearchValue, searchValue } = useSearch();

	const [page, setPage] = useState<number>(1);
	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const loadMoreData = useCallback(() => {
		if (!isFetchingGetUnassigned && page < (meta?.pageCount || 0) && page === meta.page) {
			setPage(page + 1);
		}
	}, [page, isFetchingGetUnassigned, meta]);

	useLeadsPagingSentinel({ items: inbox, sentinelRef: leadsEndRef, onLoadMore: loadMoreData });

	const [isDirty, setIsDirty] = useState<boolean>(false);

	const [isSendersDirty, setIsSendersDirty] = useState<boolean>(false);

	const ts = (key: string) => t(`messages.${key}`);

	const tabs = [
		{
			label: ts("tabs.chat"),
			value: Tabs.CHAT,
		},
		{
			label: ts("tabs.campaign"),
			value: Tabs.CAMPAIGN,
		},
	];

	const getFilterFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(params.get("filter") || "{}");
	};

	const [filter, setFilter] = useState<IFilterWithValues | undefined>(getFilterFromUrl());
	const [showLeads, setShowLeads] = useState<boolean>(true);
	const [showSingleLead, setShowSingleLead] = useState<boolean>(false);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// Mirrors assign-status/priority/signal/list-scope/filter/search selections into the URL
	// and seeds the search box from it on mount (see useReadyToSendUrlSync.ts).
	useReadyToSendUrlSync({
		selectedAssignStatus,
		priorityFilterStatus,
		signalFilterStatus,
		selectedLists,
		filter,
		query,
		setSearchValue,
		navigate,
	});

	function handleSetCurrentLead(index: number) {
		dispatch(setCurrentLeadIndex(index));
	}

	// Clear inbox and reset to page 1 when filters/search/sorting change (but not when only page changes)
	useEffect(() => {
		setPage(1);
	}, [
		dispatch,
		filter,
		type,
		query,
		sortingInbox,
		priorityFilterStatus,
		signalFilterStatus,
		selectedLists,
	]);

	// Fetch data when any parameter changes (including page)
	useEffect(() => {
		if (reviewSessionId && (isReviewSessionLoading || !reviewSession)) {
			return;
		}

		if (page === 1) {
			dispatch(clearInbox());
			dispatch(setCurrentLeadIndex(0));
		}

		// No manual abort here: a stale response (e.g. the previous tab's request landing after a
		// client-side tab switch) is ignored by the slice's request-id guard (see list-inbox.helper).
		getUnassigned(
			buildReadyToSendRequestParams({
				page,
				limit,
				query,
				sorting: sortingInbox,
				selectedAssignStatus,
				filter,
				priorityFilterStatus,
				signalFilterStatus,
				selectedLists,
				reviewSessionId,
			})
		);
	}, [
		getUnassigned,
		type,
		limit,
		page,
		query,
		sortingInbox,
		filter,
		priorityFilterStatus,
		signalFilterStatus,
		selectedLists,
		selectedAssignStatus,
		reviewSessionId,
		reviewSession,
		isReviewSessionLoading,
	]);

	const [senders, setSenders] = useState<Sender[]>(currentSenders);

	useEffect(() => {
		if (
			!senders
				.filter((sender) => sender.active)
				.every(
					(sender) =>
						currentSenders.find(
							(s) =>
								s.userId === sender.userId &&
								s.emailAddress === sender.emailAddress &&
								s.emailType === sender.emailType
						)?.active
				) ||
			!currentSenders
				.filter((sender) => sender.active)
				.every(
					(sender) =>
						senders.find(
							(s) =>
								s.userId === sender.userId &&
								s.emailAddress === sender.emailAddress &&
								s.emailType === sender.emailType
						)?.active
				)
		) {
			setIsSendersDirty(true);
		} else {
			setIsSendersDirty(false);
		}
	}, [senders, currentSenders]);

	useEffect(() => {
		setSenders(currentSenders);
	}, [currentSenders]);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (!isSequencerActive) {
				navigate(`/app/lists/`);
			}
		}, 500);
	}, [isSequencerActive, navigate]);

	const currentListPerson: ListPerson | undefined = useMemo(() => {
		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return undefined;
		}

		return inbox[currentLeadIndex];
	}, [inbox, currentLeadIndex]);

	// Placeholder ("SELECT" / "Person not found") rows have no real contact: block all
	// sending/assignment actions for them (they expose "Find leads" instead).
	const isFindLeadRow = currentListPerson ? isDummyProspect(currentListPerson) : false;

	// Valid campaigns of the current lead's list — the options shown in the "Assign to campaign" picker.
	const validCampaigns = useMemo(
		() => validCampaignsOfLead(currentListPerson),
		[currentListPerson]
	);

	// Select a campaign from the picker — drives both the read-only preview and the assign call.
	const handleSelectPickerCampaign = useCallback((campaign: CopywritingCampaign) => {
		setSelectedCampaignConfig({ ...campaign, isCustom: false });
	}, []);

	const resetDefaultCampaignConfig = useCallback(() => {
		setIsDirty(false);

		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return setSelectedCampaignConfig(undefined);
		}

		const prospect = inbox[currentLeadIndex];

		if (!prospect) {
			return setSelectedCampaignConfig(undefined);
		}

		// Resolves to undefined when the lead has no assigned/default/language-matched campaign —
		// in that case the Campaign tab renders the "No outreach yet" signpost instead of the editor.
		setSelectedCampaignConfig(resolveSelectedCampaignConfig(prospect));
	}, [inbox, currentLeadIndex]);

	useEffect(() => {
		resetDefaultCampaignConfig();
	}, [inbox, currentLeadIndex]);

	const isValidCampaignConfig = useMemo(() => {
		let isValid = true;

		if (
			!selectedCampaignConfig?.initialNodes ||
			(selectedCampaignConfig?.initialNodes && selectedCampaignConfig?.initialNodes?.length <= 1)
		) {
			return false;
		}

		function getPreviousNode(nodeId: string) {
			const previousNodeEdge = selectedCampaignConfig?.initialEdges.find(
				(edge) => edge.target === nodeId
			);

			if (!previousNodeEdge) {
				return null;
			}

			return (
				selectedCampaignConfig?.initialNodes.find((node) => node.id === previousNodeEdge.source) ??
				null
			);
		}

		if (
			!isGraphConnected(
				selectedCampaignConfig.initialNodes,
				selectedCampaignConfig.initialEdges ?? []
			)
		) {
			return false;
		}

		for (const node of selectedCampaignConfig?.initialNodes ?? []) {
			if (node.type === "emailNode") {
				let hasPrecedingEmailNode = false;
				let previousNode = getPreviousNode(node.id);

				do {
					if (previousNode?.type === "emailNode") {
						hasPrecedingEmailNode = true;
						break;
					}

					if (previousNode?.id) {
						previousNode = getPreviousNode(previousNode.id);
					} else {
						break;
					}
				} while (previousNode?.type !== "starterNode");

				if ((!node.data.subject && !hasPrecedingEmailNode) || !node.data.message) {
					isValid = false;
				}
			} else if (node.type === "linkedinNode") {
				if (!node.data.message) {
					isValid = false;
				}
			}
		}

		return isValid;
	}, [selectedCampaignConfig]);

	useEffect(() => {
		let hasAnyEmailNode = false;

		for (const node of selectedCampaignConfig?.initialNodes ?? []) {
			if (node.type === "emailNode") {
				hasAnyEmailNode = true;
				break;
			}
		}

		if (!hasAnyEmailNode && customCampaignConfig) {
			for (const node of customCampaignConfig?.initialNodes ?? []) {
				if (node.type === "emailNode") {
					hasAnyEmailNode = true;
					break;
				}
			}
		}

		if (
			hasAnyEmailNode &&
			senders.length > 0 &&
			senders?.filter((sender: Sender) => sender.active && sender.type === "email").length <= 0
		) {
			setCurrentErrors(["Campaign must have at least 1 active sender with an email address"]);
		} else {
			setCurrentErrors([]);
		}
	}, [senders, selectedCampaignConfig, customCampaignConfig]);

	const isValidCustomCampaignConfig = useMemo(() => {
		if (!customCampaignConfig) {
			return false;
		}

		return customCampaignConfig.isValid ?? false;
	}, [customCampaignConfig]);

	useEffect(() => {
		setIsLoading(true);

		setTimeout(() => {
			setIsLoading(false);
		}, 1);
	}, [currentLeadIndex, selectedCampaignConfig]);

	const handleSaveCustom = useCallback(
		async (campaignConfig: CopywritingCampaign | null) => {
			const listPersonId = inbox[currentLeadIndex]?.id;

			if (campaignConfig) {
				const { senders: _senders, ...campaignConfigWithoutSenders } = campaignConfig;

				await updateListPerson({
					id: listPersonId,
					campaignConfig: JSON.stringify(campaignConfigWithoutSenders),
				}).unwrap();
			} else {
				await updateListPerson({
					id: listPersonId,
					campaignConfig: null,
				}).unwrap();

				await updateListPersonSenders({
					id: listPersonId,
					senders: null,
				}).unwrap();
			}

			setIsDirty(false);

			if (campaignConfig === null) {
				const prospect = inbox[currentLeadIndex];
				const listCampaign = prospect ? getListCampaignConfig(prospect) : undefined;

				if (listCampaign) {
					setSelectedCampaignConfig({ ...listCampaign, isCustom: false });
				} else {
					setSelectedCampaignConfig(null);
				}
			}
		},
		[updateListPerson, updateListPersonSenders, inbox, currentLeadIndex]
	);

	const handleArchiveSingleProspect = useCallback(async () => {
		handleBulkStatusChange({
			id: currentListPerson?.list.id || 0,
			selectedItems: currentListPerson
				? [
						{
							listPersonId: currentListPerson?.id,
						},
					]
				: [],
			assignStatus: AssignStatus.ARCHIVED,
			priorityFilterStatus: priorityFilterStatus,
		});
		setShowArchiveModal(false);
	}, [currentListPerson, selectedCampaignConfig, handleBulkStatusChange]);

	const handleCheckLaterSingleProspect = useCallback(async () => {
		await handleBulkStatusChange({
			id: currentListPerson?.list.id || 0,
			selectedItems: currentListPerson
				? [
						{
							listPersonId: currentListPerson?.id,
						},
					]
				: [],
			assignStatus: AssignStatus.SAVED_FOR_LATER,
			priorityFilterStatus: priorityFilterStatus,
		});
		setShowCheckLaterModal(false);
	}, [currentListPerson, handleBulkStatusChange]);

	const handleSnoozeSingleProspect = useCallback(
		async (snoozedUntil: Date) => {
			await snoozeProspect({
				listPersonId: currentListPerson?.id || 0,
				snoozedUntil: snoozedUntil,
			}).unwrap();

			setShowSnoozeModal(false);
		},
		[snoozeProspect, currentListPerson]
	);

	// "Only pre-assign": stage the lead on the campaign by setting ONLY its campaignId. This must NOT
	// touch assignStatus — assignStatus=ASSIGNING is the send trigger (the cron assigns and, if the
	// campaign is running, schedules). Pre-assigned leads stay in Review until explicitly confirmed.
	// It sends nothing, so it starts nothing and asks nothing; "Confirm & send" asks (PREC-3296).
	const handlePreAssign = useCallback(async () => {
		if (!currentListPerson?.id || !selectedCampaignConfig?.id) {
			return;
		}

		await updateListPerson({
			id: currentListPerson.id,
			campaignId: selectedCampaignConfig.id,
		}).unwrap();

		setShowAssignPicker(false);
	}, [currentListPerson, selectedCampaignConfig, updateListPerson]);

	// "Assign & send out": move the lead into the assign pipeline (assignStatus=ASSIGNING). When the
	// chosen campaign is not sending, the user is asked whether to start it (PREC-3296) — per
	// campaign; this used to be a list-wide opt-in toggle that started every campaign on the list.
	// A cancel leaves both modals open with nothing written.
	const handleAssignAndSend = useCallback(async () => {
		const sendsAs = campaignSendersFor({
			override: configureSenders || isSendersDirty ? senders : undefined,
			prospect: currentListPerson,
			campaign: selectedCampaignConfig,
		});

		if (!(await confirmSenders(toSenderIdentities(sendsAs), 2))) {
			return;
		}

		const assigned = await assignWithActivation({
			listId: currentListPerson?.list.id || 0,
			campaignIds: selectedCampaignConfig?.id ? [selectedCampaignConfig.id] : [],
			assign: async () => {
				if (configureSenders || isSendersDirty) {
					await updateListPersonSenders({
						id: inbox[currentLeadIndex]?.id,
						senders: senders,
					}).unwrap();
				}

				await handleBulkReassignInternalCampaigns({
					id: currentListPerson?.list.id || 0,
					selectedItems:
						currentListPerson && selectedCampaignConfig?.id
							? [
									{
										listPersonId: currentListPerson?.id,
										campaignId: selectedCampaignConfig?.id,
									},
								]
							: [],
					filter: filter || {},
					inclusionStatus: InclusionFilterStatus.ADDED,
				});
			},
		});

		if (!assigned) {
			return;
		}

		setConfigureSenders(false);
		setShowConfirmModal(false);
		setShowAssignPicker(false);
	}, [
		assignWithActivation,
		currentListPerson,
		selectedCampaignConfig,
		filter,
		handleBulkReassignInternalCampaigns,
		configureSenders,
		isSendersDirty,
		senders,
		inbox,
		currentLeadIndex,
		updateListPersonSenders,
		confirmSenders,
	]);

	// "Confirm & send" (Review-to-send lead card): a pre-assigned lead already carries its campaign,
	// so skip the picker and run the same assign-and-send as the modal's "Assign & send out" — driven
	// straight from the row (row.campaignId) so it doesn't depend on the async currentLead /
	// selectedCampaignConfig state, which only settles a render after the row is selected.
	const handleConfirmAndSendRow = useCallback(
		async (row: ListPerson) => {
			if (!row?.id || !row?.campaignId) {
				return;
			}

			const campaignId = row.campaignId;
			const campaign = (row.list?.campaignConfig as Record<string, CopywritingCampaign>)?.[
				campaignId
			];
			const sendsAs = campaignSendersFor({ prospect: row, campaign });

			if (!(await confirmSenders(toSenderIdentities(sendsAs), 2))) {
				return;
			}

			await assignWithActivation({
				listId: row.list?.id || 0,
				campaignIds: [campaignId],
				assign: () =>
					handleBulkReassignInternalCampaigns({
						id: row.list?.id || 0,
						selectedItems: [{ listPersonId: row.id, campaignId }],
						filter: filter || {},
						inclusionStatus: InclusionFilterStatus.ADDED,
					}),
			});
		},
		[assignWithActivation, filter, handleBulkReassignInternalCampaigns, confirmSenders]
	);

	// Opens the "Assign to campaign" picker for the current lead — first warning if Chat drafts
	// would be superseded (pre-assigning and Chat drafts are mutually exclusive).
	const openAssignPicker = useCallback(() => {
		const hasChatDrafts =
			!!currentListPerson?.copywritingEventId &&
			currentListPerson?.copywritingStatus === CopywritingStatus.COMPLETED;

		if (hasChatDrafts && !currentListPerson?.campaignId) {
			setShowDiscardDraftsModal(true);
		} else {
			setShowAssignPicker(true);
		}
	}, [currentListPerson]);

	const goCreateCampaign = useCallback(() => {
		if (currentListPerson?.list?.id) {
			window.open(`/app/lists/create/copywriting/${currentListPerson.list.id}`, "_blank");
		}
	}, [currentListPerson]);

	// Per-row entry points for the lead-card tag + on-hover (tinder) actions. They select the row
	// first, then route to the picker / create-campaign / ignore — so they act on the hovered lead.
	const openAssignPickerForRow = useCallback(
		(row: ListPerson) => {
			handleSetCurrentLead(inbox.indexOf(row));

			// Pre-assigned lead → the card button reads "Confirm & send"; send it straight out, the
			// same as the picker's "Assign & send out", instead of reopening the picker.
			if (row.campaignId) {
				void handleConfirmAndSendRow(row);

				return;
			}

			const validCount = Object.values(
				(row.list?.campaignConfig ?? {}) as Record<string, { isValid?: boolean }>
			).filter((c) => c?.isValid).length;

			if (validCount === 0) {
				if (row.list?.id) {
					window.open(`/app/lists/create/copywriting/${row.list.id}`, "_blank");
				}

				return;
			}

			const hasChatDrafts =
				!!row.copywritingEventId && row.copywritingStatus === CopywritingStatus.COMPLETED;

			if (hasChatDrafts && !row.campaignId) {
				setShowDiscardDraftsModal(true);
			} else {
				setShowAssignPicker(true);
			}
		},
		[handleSetCurrentLead, inbox, handleConfirmAndSendRow]
	);

	const ignoreLeadForRow = useCallback(
		(row: ListPerson) => {
			handleSetCurrentLead(inbox.indexOf(row));
			setShowArchiveModal(true);
		},
		[handleSetCurrentLead, inbox]
	);

	const openCutoffModal = useCallback(() => {
		const existing = currentListPerson?.stopCampaignAt;

		// Date input expects YYYY-MM-DD.
		setCutoffDate(existing ? new Date(existing).toISOString().substring(0, 10) : "");
		setShowCutoffModal(true);
	}, [currentListPerson]);

	const handleSaveCutoff = useCallback(async () => {
		if (!currentListPerson?.id || !cutoffDate) {
			return;
		}

		const stopCampaignAt = new Date(cutoffDate).toISOString();

		await updateListPerson({ id: currentListPerson.id, stopCampaignAt }).unwrap();

		// The mutation doesn't refresh the inbox slice, so reflect the change locally
		// (merge to preserve the person's nested relations) — otherwise the button label
		// only updates after a page reload.
		dispatch(
			updatePersonInInbox({
				personId: currentListPerson.id,
				updatedPerson: { ...currentListPerson, stopCampaignAt },
			})
		);

		setShowCutoffModal(false);
	}, [currentListPerson, cutoffDate, updateListPerson, dispatch]);

	const handleRemoveCutoff = useCallback(async () => {
		if (!currentListPerson?.id) {
			return;
		}

		await updateListPerson({
			id: currentListPerson.id,
			stopCampaignAt: null,
		}).unwrap();

		dispatch(
			updatePersonInInbox({
				personId: currentListPerson.id,
				updatedPerson: { ...currentListPerson, stopCampaignAt: null },
			})
		);

		setCutoffDate("");
		setShowCutoffModal(false);
	}, [currentListPerson, updateListPerson, dispatch]);

	// The List and Feed views are single-list surfaces, so List is only reachable when exactly one
	// list is selected; Feed opens the standalone feed for whatever lists are in scope.
	const singleList = selectedLists.length === 1 ? selectedLists[0] : undefined;

	const handleSelectListView = useCallback(() => {
		if (!singleList?.id) {
			return;
		}

		navigate(`/app/lists/detail/${singleList.id}`);
	}, [singleList, navigate]);

	const listFilterElement = (
		<div className="min-w-[125px] w-[fit-content]">
			<ListScopeFilter
				{...listFilterProps}
				onApply={(value: CustomListsAutocompleteForSequencerItem[]) => {
					setSelectedLists(value);
					resetListPage();
				}}
				selectedLists={selectedLists || []}
			/>
		</div>
	);

	return (
		<>
			{accessMode === "no-senders" && <NoSenderModal />}
			<div
				className={classNames(
					"sm:block flex flex-col items-start justify-between w-full  h-full overflow-hidden",
					accessMode === "no-senders" ? "blur-sm" : ""
				)}
			>
				<div className="bg-brand-white  flex flex-col items-stretch w-full h-[calc(100vh-48px)]">
					{accessMode === "read-only" && (
						<ReadOnlyPlanBanner readOnlyUntil={lifecycle?.readOnlyUntil ?? null} />
					)}
					<ListContextHeader
						lists={listData}
						listFilter={listFilterElement}
						selectedLists={selectedLists}
						title={t("messages.pageTitle.review")}
					/>
					<div className="flex bg-white border-b border-border-light w-full flex-shrink-0">
						{tabs.map((tab, index) => (
							<button
								key={index}
								className={classNames(
									"px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
									currentTab === tab.value
										? "border-brand-light text-brand-light"
										: "border-transparent text-text-meta hover:text-text hover:border-gray-300"
								)}
								type="button"
								onClick={() => handleTabsChange(tab.value)}
							>
								{tab.label}
							</button>
						))}
					</div>
					<MessagesFilterBar
						className="border-b border-border-light"
						filterCount={filter ? Object.keys(filter).length : 0}
						onOpenFilter={() => setShowFilterModal(true)}
						searchValue={searchValue}
						onSearchChange={(value) => {
							setSearchValue(value);
							setPage(1);
						}}
						afterSearch={
							// Feed is a TRACKER-only surface, so the Review page's switcher
							// only offers List / Review (PREC-3584).
							<ListViewSwitcher
								activeView="review"
								disableList={!singleList}
								onSelectFeed={() => undefined}
								onSelectList={handleSelectListView}
								onSelectReview={() => undefined}
								showFeed={false}
							/>
						}
						dropdowns={
							<>
								<div className="w-fit min-w-[120px] max-w-[220px]">
									<DropdownWithCheckboxes
										data={toPriorityFilterItems((status) =>
											t(`inbox.priorityFilterOptions.${status}`)
										)}
										defaultValue={
											priorityFilterStatus && priorityFilterStatus.length > 0
												? priorityFilterStatus.map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL}`),
															id: PriorityFilterStatus.ALL,
														},
													]
										}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											setPage(1);

											const addedValue = values.find(
												(val) => !priorityFilterStatus.includes(val.id as PriorityFilterStatus)
											);

											const newValues = values.filter(
												(val) =>
													val.id !== PriorityFilterStatus.ALL &&
													val.id !== PriorityFilterStatus.ALL_WITH_PRIORITY
											);

											if (addedValue && addedValue.id === PriorityFilterStatus.ALL) {
												setPriorityFilterStatus([PriorityFilterStatus.ALL]);
											} else if (
												addedValue &&
												addedValue.id === PriorityFilterStatus.ALL_WITH_PRIORITY
											) {
												setPriorityFilterStatus([PriorityFilterStatus.ALL_WITH_PRIORITY]);
											} else {
												setPriorityFilterStatus(newValues.map((v) => v.id as PriorityFilterStatus));
											}
										}}
										size={ButtonSize.XS}
										title="Score"
										value={
											priorityFilterStatus && priorityFilterStatus.length > 0
												? priorityFilterStatus.map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(
																`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL_WITH_PRIORITY}`
															),
															id: PriorityFilterStatus.ALL_WITH_PRIORITY,
														},
													]
										}
									/>
								</div>
								{/* A review session drives the visible leads via its explicit listPersonIds, so the
								    status filter is meaningless here — hide it to match (PREC-3502). */}
								<div
									className={`w-fit min-w-[120px] max-w-[220px] ${reviewSessionId ? "hidden" : ""}`}
								>
									<DropdownWithCheckboxes
										data={[
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.ALL}`),
												id: AssignStatusFilter.ALL,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.DRAFT}`),
												id: AssignStatusFilter.DRAFT,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.SAVED_FOR_LATER}`),
												id: AssignStatusFilter.SAVED_FOR_LATER,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.ARCHIVED}`),
												id: AssignStatusFilter.ARCHIVED,
											},
										]}
										defaultValue={
											selectedAssignStatus && selectedAssignStatus.length > 0
												? selectedAssignStatus.map((i) => {
														return {
															title: t(`inbox.assignStatus.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.assignStatus.${AssignStatusFilter.DRAFT}`),
															id: AssignStatusFilter.DRAFT,
														},
													]
										}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											setPage(1);

											const addedValue = values.find(
												(val) => !selectedAssignStatus.includes(val.id as AssignStatusFilter)
											);

											if (addedValue && addedValue.id === AssignStatusFilter.ALL) {
												setSelectedAssignStatus([AssignStatusFilter.ALL]);
											} else {
												setSelectedAssignStatus(
													values
														.filter((value) => value.id !== AssignStatusFilter.ALL)
														.map((value) => value.id as AssignStatusFilter)
												);
											}
										}}
										size={ButtonSize.XS}
										title="Status"
										value={
											selectedAssignStatus && selectedAssignStatus.length > 0
												? selectedAssignStatus.map((i) => {
														return {
															title: t(`inbox.assignStatus.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.assignStatus.${AssignStatus.DRAFT}`),
															id: AssignStatus.DRAFT,
														},
													]
										}
									/>
								</div>
							</>
						}
					/>
					<div
						className="flex w-full flex-1 min-h-0 flex-row relative overflow-hidden"
						// overflow-hidden boxes can still be scrolled programmatically
						// (scrollIntoView, focus scrolling) and the user can never scroll
						// them back — pin the position so the top controls don't get stuck
						// clipped under the tab bar.
						onScroll={(e) => {
							e.currentTarget.scrollTop = 0;
							e.currentTarget.scrollLeft = 0;
						}}
					>
						<ReadyToSendLeadsColumn
							handleSetCurrentLead={handleSetCurrentLead}
							handleTabsChange={handleTabsChange}
							hasMorePages={!!meta && page < meta.pageCount}
							isFetching={isFetchingGetUnassigned}
							hasSearchQuery={!!query}
							leadsEndRef={leadsEndRef}
							rows={inbox}
							selectedLists={selectedLists}
							showLeads={showLeads}
							onAssignClick={openAssignPickerForRow}
							onIgnoreClick={ignoreLeadForRow}
							onToggleShowLeads={() => setShowLeads(!showLeads)}
						/>

						<div className={classNames("h-full min-h-[100%] w-full z-5 relative")}>
							<div className={classNames("h-full min-h-[100%] w-full z-5")}>
								{currentTab === Tabs.CAMPAIGN && (
									<div className="w-full h-[100%]">
										{!isLoading &&
											!isLoadingGetUnassigned &&
											inbox.length > 0 &&
											(selectedCampaignConfig ? (
												// Has a campaign assigned or a list default → show the read-only sequence.
												<GlobalNodesDataProvider>
													<CampaignMessagingOverview
														campaignConfigRaw={selectedCampaignConfig || null}
														isDirty={isDirty}
														listPerson={inbox[currentLeadIndex]}
														setCustomCampaignConfig={setCustomCampaignConfig}
														setIsDirty={setIsDirty}
														textOnly
													/>
												</GlobalNodesDataProvider>
											) : (
												// Not yet on a campaign → signpost: reach out in Chat or assign a campaign.
												(() => {
													const hasChatDrafts =
														!!currentListPerson?.copywritingEventId &&
														currentListPerson?.copywritingStatus === CopywritingStatus.COMPLETED;
													const noCampaigns = validCampaigns.length === 0;

													return (
														<SignpostCard
															actions={
																<>
																	<div className="w-fit">
																		<Button
																			color={ButtonColor.BRAND}
																			image={
																				<Icon
																					className="mr-2"
																					icon={hasChatDrafts ? faBolt : faMessage}
																				/>
																			}
																			size={ButtonSize.S}
																			title={hasChatDrafts ? ts("reviewDrafts") : ts("reachOut")}
																			onClick={() => handleTabsChange(Tabs.CHAT)}
																		/>
																	</div>
																	<div className="w-fit">
																		<Button
																			color={ButtonColor.ACTION_SECONDARY}
																			image={
																				<Icon
																					className="mr-2"
																					icon={noCampaigns ? faPlus : faShareNodes}
																				/>
																			}
																			size={ButtonSize.S}
																			title={
																				noCampaigns ? ts("createCampaign") : ts("assignToCampaign")
																			}
																			onClick={noCampaigns ? goCreateCampaign : openAssignPicker}
																		/>
																	</div>
																</>
															}
															description={
																hasChatDrafts
																	? ts("signpostDraftsDesc")
																	: ts("signpostNoOutreachDesc")
															}
															icon={hasChatDrafts ? faBolt : faShareNodes}
															title={
																hasChatDrafts ? ts("chatDraftsReady") : ts("noOutreachYetTitle")
															}
														/>
													);
												})()
											))}
									</div>
								)}
								{currentTab === Tabs.CHAT && (
									<div
										className={classNames(
											"h-full w-full z-5 relative flex flex-col overflow-hidden",
											selected.length > 0 ? "blur" : ""
										)}
									>
										<div className="flex-1 min-h-0 overflow-hidden">
											{inbox?.length > 0 && renderChatlog(selected, isLoadingGetUnassigned)}
										</div>
									</div>
								)}
							</div>
						</div>
						{type !== "inbox" && (
							<LeadDetailRail
								listId={currentListPerson?.list?.id}
								listPerson={currentListPerson}
								setShow={setShowSingleLead}
								show={showSingleLead}
							/>
						)}
					</div>

					{showFilterModal && (
						<FilterModal
							filter={filter}
							listType={ListSourceType.CSV_ENRICHMENTS}
							setFilter={(e) => {
								setPage(1);
								setFilter(e);
							}}
							setShowFilterModal={setShowFilterModal}
							showFilterModal={showFilterModal}
						/>
					)}
				</div>

				<div className="absolute mt-14 min-h-12 flex flex-row justify-end items-center border-t border-t-border px-3 py-2 rounded-t-16 bottom-0 right-0 z-[1000] bg-white w-full">
					<div className="flex flex-row flex-wrap items-center justify-end gap-2 w-full">
						<AddToTrackerFooterButton
							isLoading={bulkAddToTrackerIsLoading}
							listPersonId={currentListPerson?.id}
							organizationId={currentListPerson?.prospect?.organization?.id}
							onAdd={() => setShowAddToTrackerModal(true)}
						/>
						{selected.length === 0 && inbox[currentLeadIndex] && !isFindLeadRow ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.DANGER}
									image={<Icon className="mr-2" icon={faBan} />}
									isLoading={createBlacklistIsLoading}
									size={ButtonSize.XS}
									title={"Unsubscribe"}
									onClick={() => {
										setShowBlacklistModal(true);
									}}
								/>
							</div>
						) : (
							<></>
						)}

						{selected.length === 0 && inbox[currentLeadIndex]?.prospect?.snoozed === false ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="mr-2" icon={faClock} />}
									isLoading={isSnoozingProspect}
									size={ButtonSize.XS}
									title={"Snooze Lead"}
									onClick={() => {
										setShowSnoozeModal(true);
									}}
								/>
							</div>
						) : (
							<></>
						)}

						{selected.length === 0 &&
						inbox[currentLeadIndex]?.assignStatus === AssignStatus.DRAFT ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="mr-2" icon={faArchive} />}
									isLoading={bulkChangeStatusIsLoading}
									size={ButtonSize.XS}
									title={"Ignore Lead"}
									onClick={() => {
										setShowArchiveModal(true);
									}}
								/>
							</div>
						) : (
							<></>
						)}
						{selected.length === 0 &&
						inbox[currentLeadIndex]?.assignStatus !== AssignStatus.SAVED_FOR_LATER ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="mr-2" icon={faClock} />}
									isLoading={bulkChangeStatusIsLoading}
									size={ButtonSize.XS}
									title={"Review Later"}
									onClick={() => {
										setShowCheckLaterModal(true);
									}}
								/>
							</div>
						) : (
							<></>
						)}

						{selected.length === 0 && currentListPerson ? (
							<div className="w-fit">
								<Button
									color={
										currentListPerson.stopCampaignAt
											? ButtonColor.ACTION
											: ButtonColor.ACTION_SECONDARY
									}
									image={<Icon className="mr-2" icon={faClock} />}
									size={ButtonSize.XS}
									title={
										currentListPerson.stopCampaignAt
											? `${ts("stopsAfter")} ${new Date(currentListPerson.stopCampaignAt).toLocaleDateString()}`
											: ts("stopSendingAfterDate")
									}
									onClick={openCutoffModal}
								/>
							</div>
						) : (
							<></>
						)}

						{currentTab === Tabs.CAMPAIGN &&
						selected.length === 0 &&
						selectedCampaignConfig !== null &&
						selectedCampaignConfig !== undefined &&
						!selectedCampaignConfig.isCustom ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									image={<Icon className="mr-2" icon={faTrash} />}
									size={ButtonSize.XS}
									title="Clear sequence"
									onClick={() => handleSaveCustom(null)}
								/>
							</div>
						) : (
							<></>
						)}
						{currentTab === Tabs.CAMPAIGN &&
						selected.length === 0 &&
						selectedCampaignConfig !== null &&
						selectedCampaignConfig !== undefined &&
						isDirty ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									image={<Icon className="mr-2" icon={faUndo} />}
									size={ButtonSize.XS}
									title="Revert all changes"
									onClick={() => resetDefaultCampaignConfig()}
								/>
							</div>
						) : (
							<></>
						)}
						{currentTab === Tabs.CAMPAIGN &&
						selected.length === 0 &&
						selectedCampaignConfig !== null &&
						selectedCampaignConfig !== undefined &&
						selectedCampaignConfig.isCustom &&
						!isDirty ? (
							<div className="w-fit">
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									image={<Icon className="mr-2" icon={faTrash} />}
									size={ButtonSize.XS}
									title="Remove custom sequence"
									onClick={() => handleSaveCustom(null)}
								/>
							</div>
						) : (
							<></>
						)}
						{currentTab === Tabs.CAMPAIGN &&
							selected.length === 0 &&
							customCampaignConfig &&
							isDirty && (
								<Tooltip
									content="Please check your custom sequence configuration. It must have at least one node and each node must be filled."
									disabled={isValidCustomCampaignConfig}
								>
									<div className="w-fit">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											disabled={!isValidCustomCampaignConfig}
											image={<Icon className="mr-2" icon={faSave} />}
											size={ButtonSize.XS}
											title="Save custom sequence"
											onClick={() => handleSaveCustom(customCampaignConfig)}
										/>
									</div>
								</Tooltip>
							)}

						{currentTab === Tabs.CAMPAIGN &&
						selected.length === 0 &&
						!isFindLeadRow &&
						accessMode !== "read-only" ? (
							<Tooltip
								content={
									customCampaignConfig && !isValidCustomCampaignConfig
										? "Please check your custom sequence configuration. It has errors."
										: isDirty
											? "Please first save your custom sequence configuration."
											: ""
								}
								disabled={
									!(
										!isValidCampaignConfig ||
										isDirty ||
										!sendingUsers?.data ||
										sendingUsers?.data?.length === 0 ||
										(!!customCampaignConfig && !isValidCustomCampaignConfig)
									)
								}
							>
								<div className="w-fit">
									<Button
										color={ButtonColor.BRAND}
										disabled={
											!isValidCampaignConfig ||
											isDirty ||
											!sendingUsers?.data ||
											sendingUsers?.data?.length === 0 ||
											(!!customCampaignConfig && !isValidCustomCampaignConfig)
										}
										image={<Icon className="mr-2" icon={faPaperPlane} />}
										isLoading={bulkReassignIsLoading}
										size={ButtonSize.XS}
										title={"Send sequence"}
										onClick={() => {
											// Expand the sender config straight away only when nothing is set yet.
											setShowSenderConfig(!hasActiveSender(senders));
											setShowConfirmModal(true);
										}}
									/>
								</div>
							</Tooltip>
						) : (
							<></>
						)}

						{currentListPerson &&
							!isFindLeadRow &&
							currentTab === Tabs.CHAT &&
							accessMode !== "read-only" &&
							renderSendButtons()}
					</div>
				</div>

				{showSnoozeModal && (
					<Modal
						handleClose={() => setShowSnoozeModal(false)}
						handleSave={() => handleSnoozeSingleProspect(snoozedUntil)}
						isOpened={showSnoozeModal}
						size="xs"
						submitButtonText="Snooze"
						title="Snooze Prospect"
					>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-2">
								<div className="flex flex-col gap-2">
									Are you sure you want to snooze this prospect?
									<InputField
										handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											setSnoozedUntil(new Date(e.target.value));
										}}
										name="snoozedUntil"
										placeholder="Snooze until"
										type="datetime-local"
										value={snoozedUntil.toISOString().substring(0, 19)}
									/>
								</div>
							</div>
						</div>
					</Modal>
				)}

				{showCutoffModal && (
					<CutoffModal
						cutoffDate={cutoffDate}
						hasStopCampaignAt={!!currentListPerson?.stopCampaignAt}
						setCutoffDate={setCutoffDate}
						onClose={() => setShowCutoffModal(false)}
						onRemove={handleRemoveCutoff}
						onSave={handleSaveCutoff}
					/>
				)}

				{showArchiveModal && (
					<Modal
						handleClose={() => setShowArchiveModal(false)}
						handleSave={handleArchiveSingleProspect}
						isLoading={bulkChangeStatusIsLoading}
						isOpened={showArchiveModal}
						size="xs"
						submitButtonText="Ignore"
						title="Ignore Lead"
					>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-2">
								<div className="flex flex-col gap-2">{ts("ignoreLead")}</div>
							</div>
						</div>
					</Modal>
				)}

				{showCheckLaterModal && (
					<Modal
						handleClose={() => setShowCheckLaterModal(false)}
						handleSave={handleCheckLaterSingleProspect}
						isLoading={bulkChangeStatusIsLoading}
						isOpened={showCheckLaterModal}
						size="xs"
						submitButtonText={ts("checkLater")}
						title={ts("checkLater")}
					>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-2">
								<div className="flex flex-col gap-2">{ts("checkLeadLater")}</div>
							</div>
						</div>
					</Modal>
				)}

				{showConfirmModal && (
					<Modal
						disableSave={!hasActiveSender(senders) || currentErrors.length > 0}
						handleClose={() => {
							setShowConfirmModal(false);
							setConfigureSenders(false);
						}}
						handleSave={handleAssignAndSend}
						isLoading={bulkReassignIsLoading}
						isOpened={showConfirmModal}
						size="md"
						submitButtonText="Schedule"
						title={ts("scheduleSequence")}
					>
						<div className="flex flex-col gap-4">
							<div className="flex flex-row items-center gap-1">
								<button
									className="flex flex-row items-center gap-1 text-sm text-text-meta font-semibold"
									type="button"
									onClick={() => setShowSenderConfig((prev) => !prev)}
								>
									<Icon
										className="w-3 h-3 text-text-meta"
										icon={showSenderConfig ? faChevronUp : faChevronDown}
									/>
									{ts("configureSenders")}
								</button>
								<Information information={ts("moreThanOneSender")} />
							</div>
							{currentErrors.length > 0 && (
								<div className="flex flex-row flex-wrap gap-1">
									{currentErrors.map((error) => (
										<span
											key={error}
											className="whitespace-nowrap text-sm text-status-error bg-status-error-light rounded-full p-1 px-2"
										>
											{error}
										</span>
									))}
								</div>
							)}
							{showSenderConfig && (
								<SendersInfo
									senders={senders}
									sendingUsers={sendingUsers?.data ?? []}
									setSenders={setSenders}
								/>
							)}
						</div>
					</Modal>
				)}

				{showAssignPicker && (
					<AssignCampaignPickerModal
						campaigns={validCampaigns}
						isLoading={bulkReassignIsLoading}
						lead={currentListPerson}
						listId={currentListPerson?.list?.id ?? 0}
						selectedCampaignId={selectedCampaignConfig?.id}
						onAssignAndSend={handleAssignAndSend}
						onClose={() => setShowAssignPicker(false)}
						onPreAssign={handlePreAssign}
						onSelect={handleSelectPickerCampaign}
					/>
				)}

				{showDiscardDraftsModal && (
					<Modal
						cancelButtonText={ts("keepDrafts")}
						handleClose={() => setShowDiscardDraftsModal(false)}
						handleSave={() => {
							setShowDiscardDraftsModal(false);
							setShowAssignPicker(true);
						}}
						isOpened={showDiscardDraftsModal}
						size="xs"
						submitButtonColor={ButtonColor.DANGER}
						submitButtonText={ts("discardAndContinue")}
						title={ts("discardChatDraftsTitle")}
					>
						<div className="flex flex-col gap-4">
							<div className="text-sm text-text-meta">{ts("discardChatDraftsBody")}</div>
						</div>
					</Modal>
				)}
				{currentListPerson &&
					renderBlacklistModal({ selectedItemForConfirmation: currentListPerson })}
				{currentListPerson &&
					renderAddToTrackerModal({ selectedItems: [{ listPersonId: currentListPerson.id }] })}
				{renderFeedDraftSnackbar()}
				{activationModal}
				{senderConfirmModal}
			</div>
		</>
	);
};
